import { FormControl, styled, TextField, Typography } from '@mui/material';
import { BrandGroup } from '@provision/entity-types';
import { Editor } from '@provision/rich-text-editor';
import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { EMAIL_VALIDATION_REGEX, INTERNAL_EMAIL_VALIDATION_REGEX } from 'src/components';
import { EmailChannelSettingFormFields, EmailFormModel } from './types';

const RichTextEditor = lazy(() => import('src/components/RichTextEditor/rich-text-editor'));

const EMAIL_SIGNATURE_PLACEHOLDER = `{{Brand.displayName}}
{{Partner.supportPhone}}
{{BrandGroup.supportEmail}}

Continuing to engage with our support team implies your acceptance of our terms of service, which can be found here.`;

const StyledRichTextEditor = styled(RichTextEditor)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.custom.OV.sys.outlineVariant,
  borderRadius: theme.shape.borderRadius,
  padding: '1rem 1rem 0 1rem',

  'div.tiptap': {
    minHeight: '9rem',
  },
}));

export const ChannelsEmailForm = ({
  handleSave,
  selectedBrandGroup,
}: {
  handleSave: (data: EmailFormModel) => void;
  selectedBrandGroup: BrandGroup | null;
}) => {
  const supportEmailEditorRef = useRef<Editor | null>(null);
  const membershipEmailEditorRef = useRef<Editor | null>(null);

  const { control, setValue, register, formState, getValues, getFieldState, reset } =
    useForm<EmailFormModel>({
      mode: 'all',
      values: getFormValues(selectedBrandGroup),
    });

  /*
   * This hook reacts to brand group change on email settings page
   * When it happens we need to manually call commands on support and membership signature rich text fields to update content
   */
  useEffect(() => {
    const formValues = getFormValues(selectedBrandGroup);

    supportEmailEditorRef.current?.commands.setContent(
      formValues[EmailChannelSettingFormFields.SupportEmailSignature] ?? '',
    );

    membershipEmailEditorRef.current?.commands.setContent(
      formValues[EmailChannelSettingFormFields.MembershipEmailSignature] ?? '',
    );

    reset(getFormValues(selectedBrandGroup));
  }, [selectedBrandGroup, supportEmailEditorRef, membershipEmailEditorRef, reset]);

  const handleOnFieldBlur = useCallback(() => {
    const values = getValues();
    const changedField = Object.keys(values).find((key) => {
      const field = key as EmailChannelSettingFormFields;
      const currentValue = values[field] ?? '';
      const baselineValue = selectedBrandGroup![field] ?? '';

      return field in values && currentValue !== baselineValue;
    });

    if (changedField) {
      const { invalid } = getFieldState(changedField as EmailChannelSettingFormFields);
      if (!invalid) {
        handleSave(values);
      }
    }
  }, [getValues, selectedBrandGroup, handleSave, getFieldState]);

  return (
    <>
      <FormControl onBlur={handleOnFieldBlur} variant="standard">
        <Controller
          control={control}
          name={EmailChannelSettingFormFields.InternalEmail}
          rules={{ required: false }}
          render={({ field }) => (
            <>
              <Typography variant="subtitle1" sx={{ marginBottom: '1rem' }}>
                Internal Email for Forwarding
              </Typography>
              <TextField
                sx={{
                  alignSelf: 'flex-start',
                  minWidth: '20rem',
                }}
                label="Internal Email"
                {...register(EmailChannelSettingFormFields.InternalEmail, {
                  pattern: INTERNAL_EMAIL_VALIDATION_REGEX,
                })}
                error={!!formState.errors[EmailChannelSettingFormFields.InternalEmail]}
                helperText={
                  formState.errors[EmailChannelSettingFormFields.InternalEmail]
                    ? 'Should be a valid internal email address'
                    : ''
                }
                {...field}
              />
            </>
          )}
        />
      </FormControl>

      <FormControl onBlur={handleOnFieldBlur} variant="standard">
        <Controller
          control={control}
          name={EmailChannelSettingFormFields.SupportEmail}
          rules={{ required: false }}
          render={({ field }) => (
            <>
              <Typography variant="subtitle1" sx={{ marginBottom: '1rem' }}>
                Support Email
              </Typography>
              <TextField
                sx={{
                  alignSelf: 'flex-start',
                  minWidth: '20rem',
                }}
                label="Support Email"
                {...register(EmailChannelSettingFormFields.SupportEmail, {
                  pattern: EMAIL_VALIDATION_REGEX,
                })}
                error={!!formState.errors[EmailChannelSettingFormFields.SupportEmail]}
                helperText={
                  formState.errors[EmailChannelSettingFormFields.SupportEmail]
                    ? 'Should be a valid email address'
                    : ''
                }
                {...field}
              />
            </>
          )}
        />
      </FormControl>

      <FormControl variant="standard">
        <Controller
          control={control}
          name={EmailChannelSettingFormFields.SupportEmailSignature}
          render={({ field }) => (
            <>
              <Typography variant="subtitle1" sx={{ marginBottom: '0.3rem', marginTop: 0 }}>
                Signature
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This email signature will be applied on all outgoing emails from this address.
              </Typography>
              <Suspense>
                <StyledRichTextEditor
                  withSubmitButton={false}
                  withToolBarOtherOptions={false}
                  onBlur={() => {
                    setValue(
                      EmailChannelSettingFormFields.SupportEmailSignature,
                      supportEmailEditorRef.current?.getHTML() ?? '',
                    );
                    handleOnFieldBlur();
                  }}
                  editorOptions={{ content: field.value }}
                  placeholderOptions={{
                    placeholder: EMAIL_SIGNATURE_PLACEHOLDER,
                  }}
                  editorRef={supportEmailEditorRef}
                />
              </Suspense>
            </>
          )}
        />
      </FormControl>

      <FormControl onBlur={handleOnFieldBlur} variant="standard">
        <Controller
          control={control}
          name={EmailChannelSettingFormFields.MembershipEmail}
          rules={{ required: false }}
          render={({ field }) => (
            <>
              <Typography variant="subtitle1" sx={{ marginBottom: '1rem' }}>
                Membership Email
              </Typography>
              <TextField
                size="medium"
                sx={{
                  alignSelf: 'flex-start',
                  minWidth: '20rem',
                }}
                label="Membership Email"
                {...register(EmailChannelSettingFormFields.MembershipEmail, {
                  pattern: EMAIL_VALIDATION_REGEX,
                })}
                error={!!formState.errors[EmailChannelSettingFormFields.MembershipEmail]}
                helperText={
                  formState.errors[EmailChannelSettingFormFields.MembershipEmail]
                    ? 'Should be a valid email address'
                    : ''
                }
                {...field}
              />
            </>
          )}
        />
      </FormControl>

      <FormControl variant="standard">
        <Controller
          control={control}
          name={EmailChannelSettingFormFields.MembershipEmailSignature}
          render={({ field }) => (
            <>
              <Typography
                variant="subtitle1"
                sx={{ mt: 2, marginBottom: '0.3rem', marginTop: 0 }}
              >
                Signature
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This email signature will be applied on all outgoing emails from this address.
              </Typography>
              <StyledRichTextEditor
                withSubmitButton={false}
                withToolBarOtherOptions={false}
                onBlur={() => {
                  setValue(
                    EmailChannelSettingFormFields.MembershipEmailSignature,
                    membershipEmailEditorRef.current?.getHTML() ?? '',
                  );
                  handleOnFieldBlur();
                }}
                editorOptions={{ content: field.value }}
                editorRef={membershipEmailEditorRef}
                placeholderOptions={{
                  placeholder: EMAIL_SIGNATURE_PLACEHOLDER,
                }}
              />
            </>
          )}
        />
      </FormControl>
    </>
  );
};

function getFormValues(brandGroup: BrandGroup | null) {
  const formValues: EmailFormModel = {
    [EmailChannelSettingFormFields.Id]: brandGroup?.id ?? '',
    [EmailChannelSettingFormFields.SupportEmail]: brandGroup?.supportEmail ?? '',
    [EmailChannelSettingFormFields.MembershipEmail]: brandGroup?.membershipEmail ?? '',
    [EmailChannelSettingFormFields.SupportEmailSignature]:
      brandGroup?.supportEmailSignature ?? '',
    [EmailChannelSettingFormFields.MembershipEmailSignature]:
      brandGroup?.membershipEmailSignature ?? '',
    [EmailChannelSettingFormFields.InternalEmail]: brandGroup?.internalEmail ?? '',
  };

  return formValues;
}
